import React, { useContext, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import { BoxArrowUpRight, PencilSquare, Plus } from 'react-bootstrap-icons'
import { InfluencerContext } from '../../../../contexts/InfluencerContext'
import { formatProvider, providerLogo } from '../../../../utils/Helper'
import ReachbirdApprovedBadge from '../../../Shared/ReachbirdApprovedBadge'
import ReachbirdCollaborationModal from './ReachbirdCollaborationModal'

const formatDate = (value) => {
  if (!value) return null
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatPeriod = (start, end) => {
  const from = formatDate(start)
  const to = formatDate(end)
  if (from && to) return `${from} – ${to}`
  return from || to || '—'
}

const formatAmount = (amount, currency, symbol) => {
  if (amount === null || amount === undefined) return '—'
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'EUR', maximumFractionDigits: 2 }).format(amount)
  } catch {
    return `${symbol || currency || ''} ${Number(amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}`.trim()
  }
}

export default observer(function ReachbirdCollaborations() {
  const { influencer, reachbirdCollaborations, reachbirdApproved, reachbirdCollaborationsLoaded, getReachbirdCollaborations } = useContext(InfluencerContext)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)

  const totals = useMemo(() => {
    const byCurrency = {}
    reachbirdCollaborations.forEach(entry => {
      if (entry.payout_amount === null || entry.payout_amount === undefined) return
      const currency = entry.currency || 'EUR'
      byCurrency[currency] = (byCurrency[currency] || 0) + Number(entry.payout_amount)
    })
    return Object.entries(byCurrency)
  }, [reachbirdCollaborations])

  if (!influencer?.id) return null

  const openNew = () => {
    setEditing(null)
    setModalOpen(true)
  }

  const openEdit = (entry) => {
    setEditing(entry)
    setModalOpen(true)
  }

  return (
    <div className="bg-white dark:bg-rb-dark-mode rounded-2xl border border-rb-widget-border dark:border-rb-dark-mode-lighter shadow-sm">
      <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-rb-widget-border dark:border-rb-dark-mode-lighter">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold dark:text-white">Reachbird Collaborations</h3>
            {reachbirdApproved && <ReachbirdApprovedBadge />}
          </div>
          <p className="text-xs text-rb-lighter-grey dark:text-gray-400 mt-1">Ended campaigns with this creator in the budget table, plus past campaigns added by hand. Internal only, clients never see payouts.</p>
        </div>
        <button type="button" onClick={openNew} className="flex items-center gap-1 shrink-0 px-3 py-2 text-sm font-semibold text-white bg-rb-green rounded-lg dark:text-rb-dark-mode">
          <Plus className="text-lg" />
          Add past collaboration
        </button>
      </div>

      {!reachbirdCollaborationsLoaded && (
        <p className="px-5 py-6 text-sm text-rb-lighter-grey dark:text-gray-400">Loading collaborations…</p>
      )}

      {reachbirdCollaborationsLoaded && reachbirdCollaborations.length === 0 && (
        <p className="px-5 py-6 text-sm text-rb-lighter-grey dark:text-gray-400">No Reachbird collaborations yet. Ended campaigns appear here automatically; use "Add past collaboration" for campaigns from before the platform.</p>
      )}

      {reachbirdCollaborationsLoaded && reachbirdCollaborations.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-rb-lighter-grey dark:text-gray-400">
              <th className="px-5 py-2 font-medium">Campaign</th>
              <th className="px-3 py-2 font-medium">Brand / client</th>
              <th className="px-3 py-2 font-medium">Period</th>
              <th className="px-3 py-2 font-medium">Channels</th>
              <th className="px-3 py-2 font-medium text-right">Payout</th>
              <th className="px-5 py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {reachbirdCollaborations.map(entry => (
              <tr key={entry.key} className="border-t border-rb-widget-border dark:border-rb-dark-mode-lighter align-top">
                <td className="px-5 py-3">
                  {entry.source === 'platform' ? (
                    <Link to={`/project_manager/campaigns/${entry.campaign_id}/overview`} className="flex items-center gap-1 font-semibold text-rb-black dark:text-white hover:text-rb-green dark:hover:text-rb-green">
                      {entry.campaign_name}
                      <BoxArrowUpRight className="text-xs shrink-0" />
                    </Link>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-rb-black dark:text-white">{entry.campaign_name}</span>
                        <span className="rounded-md bg-rb-grey dark:bg-rb-dark-mode-lighter px-2 py-0.5 text-[10px] font-semibold uppercase text-rb-mat-grey dark:text-gray-400 whitespace-nowrap">Manual · pre-platform</span>
                      </div>
                      {entry.notes && <p className="mt-1 text-xs text-rb-lighter-grey dark:text-gray-400 whitespace-pre-line">{entry.notes}</p>}
                      {entry.created_by && <p className="mt-1 text-[11px] text-rb-lighter-grey dark:text-gray-500">Added by {entry.created_by}</p>}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 text-rb-black dark:text-gray-300">{entry.brand || '—'}</td>
                <td className="px-3 py-3 text-rb-black dark:text-gray-300 whitespace-nowrap">{formatPeriod(entry.start_date, entry.end_date)}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2 dark:text-white">
                    {entry.providers?.length ? entry.providers.map(provider => (
                      <span key={provider} title={formatProvider(provider)}>{providerLogo(provider)}</span>
                    )) : <span className="text-rb-lighter-grey dark:text-gray-400">—</span>}
                  </div>
                </td>
                <td className="px-3 py-3 text-right font-semibold text-rb-black dark:text-white whitespace-nowrap">{formatAmount(entry.payout_amount, entry.currency, entry.currency_symbol)}</td>
                <td className="px-5 py-3 text-right">
                  {entry.source === 'manual' && (
                    <button type="button" title="Edit" onClick={() => openEdit(entry)} className="p-1 rounded-md text-rb-mat-grey hover:bg-rb-grey dark:text-gray-400 dark:hover:bg-rb-dark-mode-lighter">
                      <PencilSquare />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-rb-widget-border dark:border-rb-dark-mode-lighter">
              <td className="px-5 py-3 text-xs text-rb-lighter-grey dark:text-gray-400" colSpan={4}>{reachbirdCollaborations.length} {reachbirdCollaborations.length === 1 ? 'collaboration' : 'collaborations'}</td>
              <td className="px-3 py-3 text-right font-semibold text-rb-black dark:text-white whitespace-nowrap">
                {totals.length ? totals.map(([currency, amount]) => <div key={currency}>{formatAmount(amount, currency)}</div>) : '—'}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      )}

      <ReachbirdCollaborationModal
        influencerId={influencer.id}
        collaboration={editing}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={getReachbirdCollaborations}
      />
    </div>
  )
})
